import { EventName } from './events/eventsName';
import { GameEvent } from './events/gameEvent';
import { SolitaireGame } from './SolitaireGame';
import { Card } from 'components/Card';
import { Store } from './store/gameStore';
import { SoundManager } from './core/soundManager';
import { GameName } from 'constants/difficulty';
import { Container, Point, Sprite, Texture } from 'pixi.js';
import gsap from 'gsap';
import { MOVE_Z_INDEX } from 'constants/layout';
import { HintItem, HintType } from './gameHints/hintGroup';
import { CARD_HINT_MOVE_TIME } from 'constants/move';
import { Layout } from './LayoutManager';
import { CardHintDisplay } from 'components/CardHintDisplay';
import { addToLevel } from './core/Playable2DRootContainer';
import { getAsset, onAssetsLoad } from './preloadGameAssets';
import { GAEvent, GAEventNames } from './analytics/events';
import { findSpiderHint } from './gameHints/SpiderHint';
import { findKlondikeHint } from './gameHints/KlondikeHints';
import { findFreeCellHint } from './gameHints/FreeCellHints';
import { shakePositions } from './helpers/shakeAnimation';
import { DebugHint } from './CustomConsole';
import { showPopup } from './showPopup';

export class Hints {
    game: SolitaireGame;
    lastPriorityCheck = 0;
    hintCards = [] as string[];
    glowScale = 420 / 363;
    glowTop = 32;
    borderTextureTop?: Texture;
    borderTextureMiddle?: Texture;
    borderTextureBottom?: Texture;
    borderTextureTopCut?: Texture;
    borderTextureBottomCut?: Texture;
    borderTextureMiddleCut?: Texture;

    constructor(game: SolitaireGame) {
        this.game = game;
        this.hint = this.hint.bind(this);
        this.onModalClose = this.onModalClose.bind(this);
        GameEvent.add(EventName.HINT, this.hint);
        GameEvent.add(EventName.MOVE, this.resetPriorityCheck.bind(this));
        GameEvent.add(EventName.CLOSE_MODAL, this.onModalClose);
        onAssetsLoad(this.createBorderTexture.bind(this));
        addEventListener('noHintNewGame', this.noHintNewGame.bind(this), false);
        addEventListener('noHintRestartGame', this.noHintRestartGame.bind(this), false);
        addEventListener('noHintKeepGame', this.noHintKeepGame.bind(this), false);
    }

    noHintNewGame() {
        document.body.classList.remove('no_hints');
        dispatchEvent(new CustomEvent('newGame'));

        GAEvent(GAEventNames.NO_HINT_NEW_GAME, {
            id: Store.gameId,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
        });
    }
    noHintRestartGame() {
        document.body.classList.remove('no_hints');
        dispatchEvent(new CustomEvent('restartGame'));

        GAEvent(GAEventNames.NO_HINT_RESTART_GAME, {
            id: Store.gameId,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
        });
    }
    noHintKeepGame() {
        Store.resumeGame();

        document.body.classList.remove('no_hints');

        GAEvent(GAEventNames.NO_KEEP_PLAY, {
            id: Store.gameId,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
        });
        Store.resumeGame();
    }

    async createBorderTexture() {
        this.borderTextureTop = await getAsset(`hints/glowTop`);
        this.borderTextureBottom = await getAsset(`hints/glowBottom`);
        this.borderTextureMiddle = await getAsset(`hints/glowMiddle`);

        this.borderTextureTopCut = await getAsset(`hints/glowTopCut`);
        this.borderTextureBottomCut = await getAsset(`hints/glowBottomCut`);
        this.borderTextureMiddleCut = await getAsset(`hints/glowMiddleCut`);
    }
    resetPriorityCheck() {
        this.hintCards = [];
        this.lastPriorityCheck = 0;
    }

    hint() {
        let hints = [] as HintItem[];

        switch (Store.name) {
            case GameName.KLONDIKE:
                hints = findKlondikeHint(this.game, this.lastPriorityCheck, this.hintCards);
                break;
            case GameName.SPIDER:
                hints = findSpiderHint(this.game, this.lastPriorityCheck, this.hintCards);
                break;
            case GameName.FREECELL:
                hints = findFreeCellHint(this.game, this.lastPriorityCheck, this.hintCards);
                break;
        }

        if (hints[0]?.type === HintType.RESET) {
            this.resetPriorityCheck();
            this.hint();
            return;
        }
        this.showConsole(hints[0]?.priority, hints[0]?.sub);

        this.game.blockMove(hints.length * CARD_HINT_MOVE_TIME * 1000);
        if (hints.length) this.showHint(hints);
        else this.showNoHint();
        Store.startGame();
        GAEvent(GAEventNames.HINT_CLICK, {
            moves: Store.movesCount,
            id: Store.gameId,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
            score: Store.score,
            timeSec: Store.time,
            timeMin: Store.timeMin,
        });
    }
    showConsole(checkPoint: number, sub: number) {
        DebugHint('Check Point: ' + checkPoint + ' : ' + sub);
    }
    showNoHint() {
        SoundManager.play('noHintSFX');
        if (Store.wandCounter === 0) {
            showPopup('out-move-ads');
            GAEvent(GAEventNames.OUT_MOVE_AD_POPUP, {
                id: Store.gameId,
                type: Store.getGameTypeText(),
                difficulty: Store.getDifficultyText(),
            });
        } else {
            showPopup('out-move-wand');
            GAEvent(GAEventNames.OUT_MOVE_WAND_POPUP, {
                id: Store.gameId,
                type: Store.getGameTypeText(),
                difficulty: Store.getDifficultyText(),
            });
        }

        Store.pauseGame();
    }
    onModalClose({ id, confirm }: { id: string; confirm: boolean }) {
        if (!confirm && ['out-move-ads', 'out-move-wand'].includes(id)) {
            this.showNoHintModal();
        }
    }

    showNoHintModal() {
        showPopup('no-hints');
        GAEvent(GAEventNames.SHOW_NO_HINT_MODAL, {
            moves: Store.movesCount,
            id: Store.gameId,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
        });
    }
    showHint(hintItems: HintItem[]) {
        if (this.lastPriorityCheck > hintItems[0].priority)
            this.hintCards = [...hintItems[0].moveGroup].map((card) => card._id);
        else this.hintCards = [...this.hintCards, ...hintItems[0].moveGroup.map((card) => card._id)];

        if (this.lastPriorityCheck != hintItems[0].priority) this.lastPriorityCheck = hintItems[0].priority;
        Store.setHintCounter(Store.hintCounter - 1);
        Store.hints++;

        GameEvent.emit(EventName.HINT_SHOW);

        const hintDuration = CARD_HINT_MOVE_TIME;
        SoundManager.play('hintSFX');
        hintItems.forEach((hintItem, groupIndex) => {
            let { moveGroup, to, type } = hintItem;
            let zIndex = MOVE_Z_INDEX - groupIndex;
            if (type === HintType.MOVE_STOCKPILE) {
                moveGroup = [moveGroup[0]];
                this.lastPriorityCheck++; //do not allow to check this condition again
            }
            if (type === HintType.RECYCLE) {
                moveGroup = [];
                zIndex = -1;
                this.lastPriorityCheck++; //do not allow to check this condition again
            }
            let moveTo = moveGroup.length
                ? (moveGroup[0].container.position as Point)
                : (to.container.position as Point);
            if (type === HintType.DEAL_SPIDER) {
                const lastStockPile = this.game.stockPile.lastCard;
                if (lastStockPile) {
                    moveTo = to.getNextCardPosition();
                }
            }
            const hintContainer = this.createHintGroup(moveGroup, type);

            hintContainer.position = moveTo;
            hintContainer.zIndex = zIndex;
            const targetPosition = to.getNextCardPosition();
            if (type !== HintType.DEAL_SPIDER) {
                gsap.to(hintContainer, {
                    x: targetPosition.x,
                    y: targetPosition.y + Layout.getCardGap().normal * groupIndex,
                    duration: hintDuration,
                    ease: 'power1.inOut',
                    delay: groupIndex * hintDuration,
                }).then(() => {
                    hintContainer.zIndex = zIndex - 2;
                });
            } else {
                this.shakeStockPile(hintContainer);
            }
            gsap.to(hintContainer, {
                alpha: 0,
                duration: hintDuration / 1.75,
                delay: hintItems.length * hintDuration,
            }).then(() => {
                hintContainer.destroy({ children: true });
            });
        });
    }
    shakeStockPile(hintContainer: Container) {
        const { x, y } = hintContainer.position;
        shakePositions.forEach((position, index) => {
            gsap.to(hintContainer, {
                x: x + position.x * 2,
                y: y + position.y * 2,
                duration: 0.1,
                delay: index * 0.1,
            });
        });
    }
    createHintGroup(moveGroup: Card[], type: HintType): Container {
        const moveContainer = new Container();
        const { width, height } = Layout.getCardSize();
        let groupHeight = 0;
        const startPosition = moveGroup.length ? moveGroup[0].position : new Point(0, 0);
        const glowContainer = new Container();
        moveContainer.addChild(glowContainer);
        moveGroup.forEach((card) => {
            const hintCard = new CardHintDisplay(card);
            groupHeight = Math.abs(startPosition.y - card.position.y);
            hintCard.container.position.y = card.position.y - startPosition.y;
            moveContainer.addChild(hintCard.container);
        });
        const glowWidth = width * this.glowScale;
        const topSprite = new Sprite(type === HintType.RECYCLE ? this.borderTextureTopCut : this.borderTextureTop);
        glowContainer.addChild(topSprite);
        topSprite.anchor.set(0.5, 0);
        topSprite.width = glowWidth;
        topSprite.position.set(0, -height / 2 - this.glowTop * topSprite.scale.x);
        topSprite.scale.y = topSprite.scale.x;

        const bottomSprite = new Sprite(
            type === HintType.RECYCLE ? this.borderTextureBottomCut : this.borderTextureBottom
        );
        glowContainer.addChild(bottomSprite);
        bottomSprite.anchor.set(0.5, 1);
        bottomSprite.width = glowWidth;
        bottomSprite.position.set(0, height / 2 + this.glowTop * bottomSprite.scale.x + groupHeight);
        bottomSprite.scale.y = bottomSprite.scale.x;

        const middleSprite = new Sprite(
            type === HintType.RECYCLE ? this.borderTextureMiddleCut : this.borderTextureMiddle
        );
        glowContainer.addChild(middleSprite);
        middleSprite.anchor.set(0.5);
        middleSprite.width = glowWidth;
        middleSprite.position.set(0, groupHeight / 2);
        middleSprite.height = groupHeight + height - height * 0.26;

        addToLevel(moveContainer);

        return moveContainer;
    }
}
